import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
import { GlobalStyles } from "@mui/system";
import { Home } from "./Home/Home";
import { About } from "./About/About";
import { Contact } from "./Contact/Contact";
import { Portfolio } from "./Portfolio/Portfolio";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AdminLogin } from "./Admin/AdminLogin";
import { HomeManagement } from "./Admin/HomeManagement";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AboutEditor } from "./Admin/AboutEditor";
import { PortfolioManager } from "./Admin/PortfolioManager";
import { PortfolioPageEditor } from "./Admin/PortfolioPageEditor";
import { CompanyInfoEditor } from "./Admin/CompanyInfoEditor";
import { BusinessAreaEditor } from "./Admin/BusinessAreaEditor";
import { MainVisualEditor } from "./Admin/MainVisualEditor";
import { ContentEditor } from "./Admin/ContentEditor";
import { ProjectTypeEditor } from "./Admin/ProjectTypeEditor";
import { FileManager } from "./Admin/FileManager";

const theme = createTheme({
  typography: {
    fontFamily: 'Pretendard, NanumSquare, sans-serif',
  },
  palette: {
    primary: {
      main: '#ff8d1d', // 필요에 따라 커스터마이징
    },
    secondary: {
      main: '#dc363a', // 필요에 따라 커스터마이징
    },
  },
});

const AdminWrapper: React.FC = () => {
  const { isAuthenticated, loading, login, logout } = useAuth();

  // 로딩 중일 때 로딩 화면 표시
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '1.2rem'
      }}>
        인증 확인 중...
      </div>
    );
  }

  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          isAuthenticated ? (
            <HomeManagement onLogout={logout} />
          ) : (
            <AdminLogin onLogin={login} />
          )
        } 
      />
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <HomeManagement onLogout={logout} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute>
            <HomeManagement onLogout={logout} />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/about" 
        element={
          <ProtectedRoute>
            <AboutEditor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/portfolio" 
        element={
          <ProtectedRoute>
            <PortfolioManager />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/portfolio-page" 
        element={
          <ProtectedRoute>
            <PortfolioPageEditor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/company-info" 
        element={
          <ProtectedRoute>
            <CompanyInfoEditor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/business-area" 
        element={
          <ProtectedRoute>
            <BusinessAreaEditor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/main-visual" 
        element={
          <ProtectedRoute>
            <MainVisualEditor title="메인 비주얼 관리" />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/content" 
        element={
          <ProtectedRoute>
            <ContentEditor section="general" title="콘텐츠 관리" />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/project-type" 
        element={
          <ProtectedRoute>
            <ProjectTypeEditor />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/files" 
        element={
          <ProtectedRoute>
            <FileManager />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalStyles
        styles={`
          @font-face {
            font-family: 'Pretendard';
            src: url('https://cdn.jsdelivr.net/gh/Project-Noonnu/noonfonts_2107@1.1/Pretendard-Light.woff') format('woff');
            font-weight: 300;
            font-style: normal;
          }
          @font-face {
            font-family: 'Pretendard';
            src: url('https://cdn.jsdelivr.net/gh/Project-Noonnu/noonfonts_2107@1.1/Pretendard-Regular.woff') format('woff');
            font-weight: 400;
            font-style: normal;
          }
          @font-face {
            font-family: 'Pretendard';
            src: url('https://cdn.jsdelivr.net/gh/Project-Noonnu/noonfonts_2107@1.1/Pretendard-Medium.woff') format('woff');
            font-weight: 500;
            font-style: normal;
          }
          @font-face {
            font-family: 'Pretendard';
            src: url('https://cdn.jsdelivr.net/gh/Project-Noonnu/noonfonts_2107@1.1/Pretendard-SemiBold.woff') format('woff');
            font-weight: 600;
            font-style: normal;
          }
          @font-face {
            font-family: 'Pretendard';
            src: url('https://cdn.jsdelivr.net/gh/Project-Noonnu/noonfonts_2107@1.1/Pretendard-Bold.woff') format('woff');
            font-weight: 700;
            font-style: normal;
          }

          html {
            line-height: 1.5;
            font-family: Pretendard, NanumSquare, sans-serif;
            scroll-behavior: smooth;
            overflow-x: hidden;
          }

          body {
            margin: 0;
            line-height: inherit;
            font-family: 'Pretendard', sans-serif;
          }

          *, ::after, ::before {
            box-sizing: border-box;
          }

          h1,h2,h3,h4,h5,h6,p {
            margin: 0;
            font-family: inherit;
          }

          a {
            color: inherit;
            text-decoration: none;
          }

          ul,ol {
            margin:0;
            padding:0;
            list-style:none;
          }
        `}
      />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/admin/*" element={<AdminWrapper />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
